import i18n from "@/i18n"
import React from "react"
import styled from "styled-components"
import { useSelector } from 'react-redux';

const Container = styled.div`
    background-color: #fff;
    display: flex;
    width: 900px;
    max-width: 100%;
    flex-direction: column;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    padding: 60px 20px 20px;
`

const MainHeading = styled.h1`
    color: #83c308;
    font-size: 34px;
    font-weight: 700;
    line-height: 44px;
    letter-spacing: -0.68px;
    text-align: center;
`

const HighlightText = styled.span`
    color: #222;
`

const SubHeading = styled.h2`
    color: #515151;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.32px;
    margin-top: 10px;
`

const AIPlusFeatures = styled.section`
    .plus-features-section {
        background-color: #fff;
        display: flex;
        width: 866px;
        max-width: 100%;
        flex-direction: column;
        overflow: hidden;
        color: #83c308;
        font-weight: 900;
        line-height: 1;
        justify-content: center;
    }

    .badge-container {
        display: flex;
        height: 37px;
        width: 100%;
        align-items: center;
        gap: 4px;
        justify-content: center;
    }

    .badge-wrapper {
        align-self: stretch;
        display: flex;
        min-width: 240px;
        width: 265px;
        align-items: start;
        gap: 16px;
        justify-content: start;
        margin: auto 0;

		&.jp {
			width: 320px;
		}

		&.en {
			width: 330px;
		}
    }

    .badge {
        border-radius: 768px;
        background-color: #222;
        position: relative;
        display: flex;
        min-width: 240px;
        min-height: 40px;
        width: 100%;
        align-items: center;
        justify-content: center;
        padding: 0 13px;
    }

    .badge-text {
        font-size: 22px;
        letter-spacing: -0.44px;
        z-index: 0;
        margin: auto 0;

        span {
            color: #fff;
        }
        .highlight {
            color: #83c308;
        }
    }

    .plus-icon {
        font-size: 15px;
        letter-spacing: -0.3px;
        position: absolute;
        z-index: 0;
        right: 64px;
        top: 2px;
        color: #83c308;

		&.jp {
			right: 58px;
    		top: -2px;
		}

		&.en {
			right: 105px;
		}
    }

    .spacer {
        background-color: #fff;
        min-height: 37px;
        margin-top: 10px;
        width: 100%;
    }
`

const AIFeatureGrid = styled.section`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    width: 1100px;
`

const AIFeatureCard = styled.article`
    background: #fff;
    display: flex;
    min-width: 240px;
    flex-direction: column;
    justify-content: center;
    width: 300px;
    padding: 17px 17px 4px;
`

const AIImageContainer = styled.figure`
    display: flex;
    min-height: 140px;
    width: 100%;
    flex-direction: column;
    overflow: hidden;
    justify-content: end;
    margin: 0;
`

const AIFeatureVideo = styled.video`
    aspect-ratio: 1.9;
    object-fit: cover;
    object-position: center;
    width: 100%;
    border-radius: 12px;
    flex: 1;
`

const AIFeatureContent = styled.header`
    align-self: start;
    display: flex;
    margin-top: 12px;
    flex-direction: column;
    justify-content: center;
`

const AIFeatureTitle = styled.h3`
    color: #222;
    font-size: 16px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.32px;
    text-align: left;
    align-self: start;
    margin: 0;
`

const AIFeatureDescription = styled.p`
    text-align: left;
    color: #515151;
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: -0.26px;
    margin: 9px 0 0;
    white-space: pre-line;
`

function AIFeature() {
    const featureData = [
        {
            title: i18n.t("common.ai.tab.remove.background"),
            description: i18n.t("common.landing.ai.feature.des_1"),
            src: "https://front.videomonster.com/videos-7cuytg20/ai/1_remove_bg.mp4"
        },
        {
            title: i18n.t("common.ai.tab.upscale"),
            description: i18n.t("common.landing.ai.feature.des_2"),
            src: "https://front.videomonster.com/videos-7cuytg20/ai/2_upscaling.mp4"
        },
        {
            title: i18n.t("common.ai.tab.erase.object"),
            description: i18n.t("common.landing.ai.feature.des_3"),
            src: "https://front.videomonster.com/videos-7cuytg20/ai/3_erase_object.mp4"
        },
        {
            title: i18n.t("common.ai.tab.change.background"),
            description: i18n.t("common.landing.ai.feature.des_4"),
            src: "https://front.videomonster.com/videos-7cuytg20/ai/4_change_bg.mp4"
        },
        {
            title: i18n.t("common.ai.tab.extend.img"),
            description: i18n.t("common.landing.ai.feature.des_5"),
            src: "https://front.videomonster.com/videos-7cuytg20/ai/5_extend_image.mp4"
        },
        {
            title: i18n.t("common.ai.tab.generate.copy"),
            description: i18n.t("common.landing.ai.feature.des_6"),
            src: "https://front.videomonster.com/videos-7cuytg20/ai/6_generate_copy.mp4"
        }
    ]

    return (
        <AIFeatureGrid>
            {featureData.map((feature) => (
                <AIFeatureCard>
                    <AIImageContainer>
                        <AIFeatureVideo
                            src={feature.src}
                            muted
                            autoPlay
                            loop
                            playsInline
                            webkit-playsinline
                        />
                    </AIImageContainer>
                    <AIFeatureContent>
                        <AIFeatureTitle>{feature.title}</AIFeatureTitle>
                        <AIFeatureDescription className="feature-description">{feature.description}</AIFeatureDescription>
                    </AIFeatureContent>
                </AIFeatureCard>
            ))}
        </AIFeatureGrid>
    )
}

function PlusFeaturesBadge() {
	const lang = useSelector(state => state.langStore.lang);
    return (
        <AIPlusFeatures>
            <section className="plus-features-section">
                <div className="badge-container">
                    <div className={`badge-wrapper ${lang}`}>
                        <div className="badge">
                            <h2 className="badge-text">
                                <span className="highlight">{i18n.t("common.landing.top.banner.videomonster")}{" "}</span>
                                <span>{i18n.t("common.landing.top.banner.plus.func")}</span>
                            </h2>
                            <span className={`plus-icon ${lang}`}>+</span>
                        </div>
                    </div>
                </div>
                <div className="spacer" />
            </section>
        </AIPlusFeatures>
    )
}

export default function AISection() {
    return (
        <>
            <Container>
                <MainHeading>
                    {i18n.t("common.landing.ai.feature.heading_1")} <br />
                    <HighlightText>{i18n.t("common.landing.ai.feature.heading_2")}</HighlightText>
                </MainHeading>
                <SubHeading>{i18n.t("common.landing.ai.feature.heading_3")}</SubHeading>
            </Container>
            <PlusFeaturesBadge />
            <AIFeature />
        </>
    )
}